import { useCallback, useEffect, useState } from 'react';
import { AppBar } from '../components/AppBar';
import { QueueStatus } from '../components/QueueStatus';
import { Icon } from '../components/icons';
import { listPartiallyScanned } from '../api/endpoints';
import { ApiError } from '../api/types';
import type { OverviewShipment } from '../domain/routes';
import { todayIso } from '../domain/dates';
import { useT } from '../i18n/I18nProvider';

interface Props {
  onBack: () => void;
  onLogout: () => void;
}

/**
 * Rozskenované zásilky — objednávky, u kterých někdo začal a nedodělal je.
 *
 * Řádky jsou schválně jen ke čtení, stejně jako v detailu trasy. Skladník
 * podle nich dojde k regálu a chybějící kus **naskenuje** — otevřít zásilku
 * tapnutím by obešlo sken, a tím i celý zápis „Načteno skladem“.
 */
export function PartialScreen({ onBack, onLogout }: Props) {
  const { t } = useT();
  const [items, setItems] = useState<OverviewShipment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setItems(null);
    setError(null);
    try {
      setItems(await listPartiallyScanned(todayIso()));
    } catch (err) {
      console.error('[partial] načtení rozskenovaných zásilek selhalo:', err);
      setError(err instanceof ApiError && err.kind === 'FORBIDDEN' ? t('routesForbidden') : t('partialErr'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="app">
      <AppBar onLogout={onLogout} />

      <div className="screen-pad">
        <QueueStatus />

        <div className="rt-head">
          <h2 className="h2">{t('partialTitle')}</h2>
          <button className="btn ghost" type="button" onClick={onBack}>
            {t('partialBack')}
          </button>
        </div>

        <p className="p">{t('partialHint')}</p>

        {error && <p className="status err">{error}</p>}
        {!error && items === null && <p className="status">{t('partialLoading')}</p>}
        {!error && items !== null && items.length === 0 && <p className="status">{t('partialEmpty')}</p>}

        {(items ?? []).map((s) => (
          <PartialRow key={s.shipmentId} shipment={s} />
        ))}
      </div>
    </div>
  );
}

function PartialRow({ shipment }: { shipment: OverviewShipment }) {
  const { t } = useT();
  const missing = shipment.parcels.filter((p) => p.whScannedAt === null && p.loadedAt === null);

  return (
    <div className="sh sh-incomplete">
      <div className="sh-top">
        {/* Backend vrací i zásilky, které plánovač ještě nepřiřadil na vůz
            (`routeNo: null`). Prázdné místo by vypadalo jako rozbitý layout —
            a hlavně: právě tahle zásilka se v hale ztratí nejspíš ze všech,
            protože ji nehlídá ani nakládka. */}
        {shipment.routeNo ? (
          <span className="sh-seq">{shipment.routeNo}</span>
        ) : (
          <span className="sh-seq sh-seq-none">{t('partialNoRoute')}</span>
        )}
        <div className="sh-id">
          <span className="id mono">{shipment.shipmentNumber}</span>
          {shipment.recipientName && <span className="who">{shipment.recipientName}</span>}
          {shipment.address && <span className="who-sub">{shipment.address}</span>}
        </div>
      </div>

      <div className="sh-state">
        <Icon name="warn" />
        <span>
          {t('routeScanned')} {shipment.scannedCount} / {shipment.parcelCount}
        </span>
      </div>

      {missing.length > 0 && (
        <div className="sh-missing">
          {t('shMissing')}: {missing.map((p) => p.packageNumber).join(', ')}
        </div>
      )}
    </div>
  );
}
